/**
 * @leadership/api-client — shared HTTP client used by both the web app and
 * the mobile app.
 *
 * Every authenticated request goes through `client.request(path, options)`,
 * which prepends the base URL, attaches a Firebase ID token as a Bearer
 * header, parses JSON, and throws on non-2xx responses with the API's
 * `error` message if present.
 *
 * The two platforms diverge only in how they bootstrap auth and where the
 * base URL comes from. Each creates an instance at startup:
 *
 *   // Web (apps/web/src/firebase-init.ts)
 *   export const apiClient = createApiClient({
 *     baseUrl: window.API_BASE_URL,
 *     getToken: () => window.firebaseAuth.currentUser?.getIdToken() ?? null,
 *   })
 *
 *   // Mobile (apps/mobile/src/data/apiClient.ts)
 *   export const apiClient = createApiClient({
 *     baseUrl: API_BASE_URL,
 *     getToken: () => auth.currentUser?.getIdToken() ?? null,
 *   })
 */

export interface ApiClientOptions {
  baseUrl: string
  /**
   * Returns a Firebase ID token, or null if the user isn't signed in.
   * Called once per request so tokens refresh naturally.
   */
  getToken: () => Promise<string | null> | string | null
  /**
   * Optional override for `fetch` — useful for tests and for platforms
   * that need a polyfilled or instrumented fetch.
   */
  fetch?: typeof fetch
}

export interface ApiClient {
  request: <T = unknown>(path: string, options?: RequestInit) => Promise<T>
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export function createApiClient(opts: ApiClientOptions): ApiClient {
  const fetchImpl = opts.fetch ?? fetch
  return {
    async request<T = unknown>(path: string, options: RequestInit = {}): Promise<T> {
      const token = await opts.getToken()
      if (!token) throw new Error('Not authenticated')

      const res = await fetchImpl(`${opts.baseUrl}${path}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...options.headers,
        },
      })

      // Try to parse JSON — responses from the API always return JSON,
      // even on error. If parsing fails, surface a generic message.
      let data: any
      try {
        data = await res.json()
      } catch {
        if (!res.ok) throw new ApiError(`HTTP ${res.status}`, res.status, null)
        return undefined as T
      }

      if (!res.ok) {
        throw new ApiError(data?.error || `HTTP ${res.status}`, res.status, data)
      }
      return data as T
    },
  }
}
